import { Component, inject, Input } from '@angular/core';
import { FilterService } from '../../../../../_shared/services';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-consumption-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './fuel-consumption-widget.component.html',
  styleUrl: './fuel-consumption-widget.component.scss'
})
export class FuelConsumptionWidgetComponent extends BaseWidgetComponent {
  @Input() selectedFilter: string;

  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private auth = inject(AuthService);
  private transloco = inject(TranslocoService);

  cost: number = 0;
  hasError: boolean = false;
  errorMessage: string = '';
  currentDate: Date = new Date();
  currencySymbol: string = '';
  currencyCode: string = '';

  selectedUnit: string = MeasurementTypes.Volume.toString();
  dropdownOptions: DropdownOption[] = []

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.percentageDifference = 0;
    this.difference = 0;

    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.dropdownOptions = [
      { label: this.transloco.translate('executiveStats.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
      { label: this.transloco.translate('executiveStats.units.kilometresPerLitre'), value: MeasurementTypes.FuelEconomy.toString(), unit: 'km/ℓ' },
      { label: this.transloco.translate('executiveStats.units.litresPerHundredKilometres'), value: MeasurementTypes.FuelConsumption.toString(), unit: 'ℓ/100km' },
      { label: `${this.currencySymbol}/Km`, value: MeasurementTypes.CostPerUnit.toString(), unit: '/km' }
    ];
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const request: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        measurementType: getEnumFromString(this.selectedUnit)
      }

      this.cancelSubject.next();
      const response = await this.executiveStatsService.getFuelConsumption(request, this.cancelSubject);
      this.value = response.data?.totalValue ?? 0;
      this.percentageDifference = response.data?.percentageDifference ?? 0;
      this.cost = response.data?.cost ?? 0;
      this.difference = response.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;

    await this.onFilterChangeAsync();
  }
}